/**
 * Buying credits, in a browser.
 *
 * Stripe Checkout, never RevenueCat: react-native-purchases is a native module and an IAP
 * button in a browser is a dead end. The server owns the money — POST /billing/checkout
 * mints a session bound to the caller's Cognito sub, and the signed webhook is what
 * actually grants credits. This only asks for the session and hands the browser over.
 */
import { ApiError, request } from './client';
import { cardLater } from '../analytics/experiment';
import type { Founding } from '../state/plans';
import type { AppPlan } from '@/webapp/plan/planTypes';
import { TRIAL_DAYS } from '../state/trial';
import type { PlanId } from '../state/plans';

export type CheckoutResult =
  | { status: 'redirecting'; url: string }
  | { status: 'signin_required' }
  /**
   * `reason` is the server's refusal token when there was one. `already_subscribed` is worth
   * acting on and not only showing: it can mean the server has just confirmed, with Stripe, a
   * subscription this screen did not know of — see Credits.tsx.
   */
  | { status: 'error'; message: string; reason?: string };

/**
 * What is being bought.
 *
 * A subscription for somebody with no plan; the one-time top-up for a subscriber whose month
 * has run out. "Get more credits" only ever asked for the first, so a subscriber bought a
 * SECOND plan, and the first cancellation of the two then dropped them to free while they
 * paid for the other. The server now refuses that, and this names what is wanted.
 */
export type CheckoutKind = 'subscription' | 'topup';

/*
 * The server's refusals are machine tokens, which `client.ts` puts in `reason` — and a 409's
 * message IS that token. Shown as they are, "already_subscribed" is a word nobody can act on in
 * the one place that asks for money.
 */
const CHECKOUT_REFUSALS: Record<string, string> = {
  already_subscribed: 'You already have a subscription. Extra credits are the way to get more this month.',
  topup_unavailable: "Extra credits aren't on sale yet. Your credits refill when your plan renews.",
  // Two presses at once — two tabs, or a phone and the web: the server makes one session (review, 2026-09-30).
  checkout_in_progress: 'A checkout is already opening in another tab or on another device. Finish it there, or try again in a minute.',
  // The trial is not named here: it is one per account (ac26ee7), so this is the sentence for an
  // account that may not have one. `refusalFor` names it only when the caller knows it can.
  subscription_required: 'Extra credits are for subscribers. Choose a plan to get credits every month.',
  // The plans (2026-10-27): a name the server does not know, or a price the deploy has not set yet.
  unknown_plan: 'That plan isn’t on sale. Choose another, or try again later.',
  plan_unavailable: 'That plan isn’t on sale just now. Try again in a little while.',
};

/**
 * The refusal in words, for this account. A top-up refused for want of a subscription promised
 * "the 7-day free trial" to everybody, including an account that had had its week and would be
 * charged on the spot (F23, 2026-09-30). The trial is named only when `trialAvailable` is true;
 * absent promises nothing.
 */
function refusalFor(reason: string, options: CheckoutOptions): string | undefined {
  if (reason === 'subscription_required' && options.trialAvailable === true) {
    return `Extra credits are for subscribers. Start a ${TRIAL_DAYS}-day free trial of a plan to get credits every month.`;
  }
  return CHECKOUT_REFUSALS[reason];
}

/**
 * `resume: 'build'` is the Plan screen's Build button: the server sends that checkout back to
 * /billing/success?…&resume=build, which starts the build from the draft put away before the
 * redirect, and a cancel back to /new/plan?checkout=cancelled. The shared contract with the
 * server (2026-10-27): `{ kind: 'subscription', plan, resume?: 'build' }`. The plan is required
 * for a subscription from here: a checkout that names none is sold the old single plan, which
 * the web no longer offers (the iOS app's paywall still sends none).
 */
export interface CheckoutOptions {
  resume?: 'build';
  /** From /credits: whether this account can still have the trial. Only words depend on it. */
  trialAvailable?: boolean;
  /** The plan chosen (state/plans.ts). */
  plan?: PlanId;
}

export function startCheckout(
  kind: 'subscription',
  redirect: ((url: string) => void) | undefined,
  options: CheckoutOptions & { plan: PlanId },
): Promise<CheckoutResult>;
export function startCheckout(
  kind: 'topup',
  redirect?: (url: string) => void,
  options?: CheckoutOptions,
): Promise<CheckoutResult>;
export async function startCheckout(
  kind: CheckoutKind,
  redirect: (url: string) => void = (url) => window.location.assign(url),
  options: CheckoutOptions = {},
): Promise<CheckoutResult> {
  try {
    const body = {
      kind,
      ...(kind === 'subscription' && options.plan ? { plan: options.plan } : {}),
      // The no-card trial's half (analytics/experiment.ts `trial_card`): the server takes the card later
      // only for a full 14-day trial, the case the plan choice said it for.
      ...(kind === 'subscription' && cardLater() ? { card: 'later' } : {}),
      ...(options.resume ? { resume: options.resume } : {}),
    };
    const { url } = await request<{ url?: string }>('/billing/checkout', { method: 'POST', body });
    // Navigating to undefined would land someone on a blank page mid-purchase.
    if (!url) return { status: 'error', message: 'Could not start checkout.' };
    redirect(url);
    return { status: 'redirecting', url };
  } catch (err) {
    if (err instanceof ApiError) {
      // A dead button is worse than a prompt they can act on.
      if (err.kind === 'unauthorized') return { status: 'signin_required' };
      /*
       * The token may be in either field: older routes put it in `error` (client.ts `reason`), the
       * plans' refusals in `reason` (client.ts `code`) beside a sentence for logs.
       */
      const token = err.code && CHECKOUT_REFUSALS[err.code] ? err.code : err.reason;
      const refusal = token ? refusalFor(token, options) : undefined;
      return { status: 'error', message: refusal ?? err.message, ...(token ? { reason: token } : {}) };
    }
    return { status: 'error', message: 'Could not start checkout.' };
  }
}

export type ProResult = { status: 'sent' } | { status: 'signin_required' } | { status: 'error'; message: string };

/**
 * Pro's "Talk to us" (2026-10-27): `POST /billing/pro`, signed in. The server logs `pro_enquiry`
 * and emails the owner the account's email and the app's name; nothing is sold here. The app name
 * is the one on screen, when there is one — never the idea, which is somebody's own words.
 */
export async function requestPro(appName?: string): Promise<ProResult> {
  try {
    const name = appName?.trim();
    await request<{ ok?: boolean }>('/billing/pro', { method: 'POST', body: name ? { appName: name } : {} });
    return { status: 'sent' };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'unauthorized') return { status: 'signin_required' };
    return { status: 'error', message: 'That didn’t reach us. Try again, or email hello@saddle-sites.com.' };
  }
}

export interface CheckoutOffers {
  /** Whether a top-up is on sale, and — when the server says — how many credits it adds. */
  topup: { available: boolean; credits?: number };
  /** The founding offer while any are left (2026-10-29; state/plans.ts Founding). */
  founding?: Founding;
}

/**
 * What is on sale, asked BEFORE a button is drawn for it: `GET /billing/checkout`.
 *
 * The top-up exists only once the owner has created its one-time price in Stripe. Until then a
 * "Buy 10,000 credits" button could only answer "not available" after it was pressed. Any
 * failure — no connection, a server deployed before this route existed — reads as NOT on sale:
 * the absent case is the safe one, because a missing button costs nothing and a broken one
 * costs trust. Not retried, for the same reason.
 */
export async function checkoutOffers(): Promise<CheckoutOffers> {
  try {
    const data = await request<{ topup?: { available?: unknown; credits?: unknown }; founding?: { left?: unknown; percentOff?: unknown; months?: unknown } }>('/billing/checkout', {
      method: 'GET',
      retry: false,
    });
    const f = data?.founding;
    const founding = f && typeof f.left === 'number' && f.left > 0 && typeof f.percentOff === 'number' && typeof f.months === 'number'
      ? { founding: { left: f.left, percentOff: f.percentOff, months: f.months } }
      : {};
    const topup = data?.topup;
    if (topup?.available !== true) return { topup: { available: false }, ...founding };
    return { topup: { available: true, ...(typeof topup.credits === 'number' ? { credits: topup.credits } : {}) }, ...founding };
  } catch {
    return { topup: { available: false } };
  }
}

/**
 * The balance, and whether the free first build is still there.
 *
 * `firstBuildDone` has always been in this response and this client dropped it on the floor
 * — the same shape as the phone's bug, where the app could not say "this one is free"
 * because it had never read the field that says so. Optional in the type because an older
 * server would not send it, and absent must read as "unknown" rather than as "spent".
 *
 * `subscribed` is whether a store is BILLING the account, which `tier` does not say: a promo code
 * puts an account on the paid tier with no plan behind it. What to sell somebody — Subscribe or
 * the top-up — is decided on this. Optional for the same reason, and absent is "not known".
 */
export interface CreditBalance {
  creditsRemaining: number;
  tier: string;
  firstBuildDone?: boolean;
  subscribed?: boolean;
  /**
   * Whether the next subscription checkout is the trial: one per account, so an account that
   * has had it is sold the plan from day one. Absent — a server from before the field — is the
   * trial (state/trial.ts `offerFor`).
   */
  trialAvailable?: boolean;
  /**
   * The plan this account is on (2026-10-27, state/plans.ts). Absent for no plan, and for a
   * subscriber on the older single plan (`subscribed` says that one).
   */
  plan?: PlanId;
  /** ISO: when the trial ends and the card is first charged, while in one. */
  trialEndsAt?: string;
  /** ISO: set once they have cancelled; the plan lasts until then. */
  cancelAt?: string;
  /** A trial begun without a card, with none added yet (the no-card test, 2026-11-01): "Add a card". */
  needsCard?: boolean;
  /** A price made for this account (infra shared/personalOffer.ts), while it has no plan: cents a period, for so many months. */
  personalOffer?: { plan: PlanId; amount: number; months: number };
  /**
   * Gift credits (infra/lambda/shared/gift.ts): what is left and until when, or that a gift ended.
   * Absent for an account never gifted. While it has a balance, Build builds (it is credits).
   */
  gift?: { remaining: number; expiresAt: string | null; ended: 'used_up' | 'expired' | null };
  /**
   * V6 (2026-10-14): the free week this account's first app went live on, and which app (the web
   * app's own id). Set once and never cleared, so it is also there after the week has ended; an
   * account with one has `trialAvailable: false`; while the week runs it is offered the trial of a
   * plan that first charges on the week's last day (`offerFor`, `freeWeekEnd`).
   */
  freeWeek?: { until: string; appId: string };
}

export async function creditBalance(): Promise<CreditBalance> {
  return request<CreditBalance>('/credits');
}

export type PortalResult =
  | { status: 'redirecting'; url: string }
  | { status: 'signin_required' }
  /** No Stripe customer at all — they subscribed through the App Store. A normal answer. */
  | { status: 'managed_elsewhere' }
  | { status: 'error'; message: string };

/**
 * The door out. Opens Stripe's billing portal so a subscriber can actually cancel.
 *
 * This closes a promise the product was already making: strings.ts has been telling web
 * customers they can "cancel anytime from your account" with nothing behind it. A
 * subscription you cannot leave from where you were told you could leave it is the kind of
 * thing that is both a support burden and, in several places, a legal one.
 *
 * Three answers that are not errors, and one that is:
 *  - 404 `no_stripe_subscription` means this person pays through the App Store. That is a
 *    FACT about them, not a failure, so they are told where to go rather than shown a
 *    problem to solve.
 *  - 502 carries Stripe's own sentence, which is the useful part: the common one is "No
 *    configuration provided", meaning the portal is not enabled in the Stripe dashboard.
 *    Replacing that with "the server had a problem" would hide the one instruction anybody
 *    could act on, so it is surfaced verbatim.
 *  - 401 asks them to sign in, rather than presenting a dead button.
 *
 * Someone who subscribed before the customer id was stored is found by an email lookup on
 * the first visit and remembered after that, so the FIRST call for such a person is slow.
 * That is why this gets a longer deadline than an ordinary data call.
 */
const PORTAL_TIMEOUT_MS = 30_000;

export async function openBillingPortal(
  redirect: (url: string) => void = (url) => window.location.assign(url),
): Promise<PortalResult> {
  try {
    const { url } = await request<{ url?: string }>('/billing/portal', {
      method: 'POST',
      timeoutMs: PORTAL_TIMEOUT_MS,
    });
    if (!url) return { status: 'error', message: 'Could not open the billing portal.' };
    redirect(url);
    return { status: 'redirecting', url };
  } catch (err) {
    if (!(err instanceof ApiError)) return { status: 'error', message: 'Could not open the billing portal.' };
    if (err.kind === 'unauthorized') return { status: 'signin_required' };
    /*
     * Read off `reason`, not `code`: this route puts its machine token in the body's
     * `error` field, which client.ts warns is used BOTH ways across the API. Branching on
     * `code` here would never fire, and everyone would be told to check their connection.
     */
    if (err.kind === 'not_found' && err.reason === 'no_stripe_subscription') {
      return { status: 'managed_elsewhere' };
    }
    // Stripe's own words for a gateway failure; ours for anything else.
    if (err.status === 502 && err.reason) return { status: 'error', message: err.reason };
    return { status: 'error', message: err.message };
  }
}

/** An app already built for the person a personal code was made for (infra shared/personalCodes.ts). */
export interface PreparedApp {
  appId: string;
  appName: string;
  goal: string;
  plan: AppPlan | null;
  appCode: string;
  html: string;
  /** Their saved, unfinished project to finish into; absent, a new one. */
  projectId?: string;
}

export type RedeemResult =
  /**
   * `creditsRemaining` is the balance AFTER the code, when the server says it. Absent is left
   * absent rather than read as 0: a screen showing "0" to someone who just added credits is the
   * bug the Credits screen's "—" exists to prevent.
   */
  | {
    status: 'redeemed';
    creditsRemaining?: number;
    granted: number;
    tier?: string;
    /** A personal code's free stretch: gift credits until this date (infra shared/personalCodes.ts). */
    gift?: { remaining: number; expiresAt: string | null };
    /** An app built for this person, for this browser to finish into their account (buildRun `prebuilt`). */
    prepared?: PreparedApp;
    /** Redeemed before by this account: nothing granted again; the app is handed back to finish. */
    alreadyRedeemed?: boolean;
  }
  /**
   * `reason` is the server's own token ('invalid_code', 'already_redeemed', 'code_expired',
   * 'code_exhausted', 'promo_unavailable', 'signup_required') or 'rate_limited', or the client's
   * error kind for anything else. The screen turns it into words with the phone's own
   * `redeemFailureMessage`, which answers an unknown one with the generic sentence.
   */
  | { status: 'refused'; reason: string };

/**
 * Redeem a promo code: `POST /redeem` with `{ code }`, the call the phone makes (guidedClient.ts).
 *
 * On the LLM Function URL, not the API gateway: /redeem is a route of the LLM proxy and the
 * gateway has no resource for it, so `base: 'api'` would answer 403/404 for every code. The phone
 * reaches it the same way, through EXPO_PUBLIC_LLM_URL.
 *
 * Sent once and never retried (the client only repeats a GET), though the server would refuse a
 * second redemption anyway: it claims a per-account marker before it grants anything.
 */
export async function redeemCode(code: string): Promise<RedeemResult> {
  try {
    const data = await request<{ creditsRemaining?: number; granted?: number; tier?: string; gift?: { remaining: number; expiresAt: string | null }; prepared?: PreparedApp; alreadyRedeemed?: boolean }>('/redeem', {
      base: 'llm',
      method: 'POST',
      body: { code: code.trim() },
    });
    return {
      status: 'redeemed',
      granted: data?.granted ?? 0,
      ...(typeof data?.creditsRemaining === 'number' ? { creditsRemaining: data.creditsRemaining } : {}),
      ...(data?.tier ? { tier: data.tier } : {}),
      ...(data?.gift ? { gift: data.gift } : {}),
      ...(data?.prepared ? { prepared: data.prepared } : {}),
      ...(data?.alreadyRedeemed ? { alreadyRedeemed: true } : {}),
    };
  } catch (err) {
    if (!(err instanceof ApiError)) return { status: 'refused', reason: 'unknown' };
    /*
     * The status decides these two, whatever the body says: a 401 from this route is always
     * "no account", and a 429 may come from Lambda's own throttle, whose body is not ours.
     */
    if (err.kind === 'unauthorized') return { status: 'refused', reason: 'signup_required' };
    if (err.kind === 'rate_limited') return { status: 'refused', reason: 'rate_limited' };
    // The machine token is in the body's `error` field on this route, which the client keeps
    // as `reason` (see client.ts on why that field is read per route).
    return { status: 'refused', reason: err.reason ?? err.kind };
  }
}

/**
 * "Add a card" for a trial begun without one (the no-card test, 2026-11-01): a Stripe page that saves a
 * card and nothing else, back to Account. Resolves only when it could not go.
 */
export async function addCard(redirect: (url: string) => void = (url) => window.location.assign(url)): Promise<CheckoutResult> {
  try {
    const { url } = await request<{ url?: string }>('/billing/checkout', { method: 'POST', body: { kind: 'card' } });
    if (!url) return { status: 'error', message: 'Could not open the card page.' };
    redirect(url);
    return { status: 'redirecting', url };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'unauthorized') return { status: 'signin_required' };
    return { status: 'error', message: 'Could not open the card page. Try again in a moment.' };
  }
}
